import { useEffect, useState } from "react";
import Confetti from "react-confetti";
import { useWindowSize } from "react-use";
import { PartyPopper, Trophy, Sparkles } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { getVisitorId } from "../utils/analytics";
import { flagOf } from "../pages/Leaderboard";
import sfx from "../utils/sound";

const BASE = process.env.REACT_APP_BACKEND_URL;

const secondsUntilNextSundayUtc = () => {
  const now = new Date();
  const daysUntilSunday = (7 - now.getUTCDay()) % 7 || 7;
  const target = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + daysUntilSunday, 0, 0, 0));
  return Math.max(0, Math.floor((target - now) / 1000));
};

const fmtCountdown = (secs) => {
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

// Weekly Reset Party — shows the current week's top-3 playtime, a live
// countdown to the next Sunday reset, and (on Sundays only) a Claim Sunday
// bonus button for the top-3 kids. Once claimed the visitor is granted
// +250 coins and the "weekly-champion" sticker.
const WeeklyResetParty = () => {
  const { addCoins, addSticker } = useStore();
  const { width, height } = useWindowSize();
  const [data, setData] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [confetti, setConfetti] = useState(false);
  const [countdown, setCountdown] = useState(secondsUntilNextSundayUtc());

  const refresh = async () => {
    try {
      const vid = encodeURIComponent(getVisitorId());
      const r = await fetch(`${BASE}/api/leaderboard/weekly?visitor_id=${vid}`);
      const j = await r.json();
      setData(j);
    } catch {
      /* best-effort */
    }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(() => setCountdown(secondsUntilNextSundayUtc()), 60_000);
    return () => clearInterval(t);
  }, []);

  const claim = async () => {
    if (!data?.can_claim || claiming) return;
    setClaiming(true);
    try {
      const r = await fetch(`${BASE}/api/leaderboard/weekly/claim`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ visitor_id: getVisitorId() }),
      });
      const j = await r.json();
      if (r.ok && !j.already_claimed) {
        addCoins(j.coins || 0);
        if (j.sticker && addSticker) addSticker(j.sticker);
        sfx.fanfare();
        setConfetti(true);
        setTimeout(() => setConfetti(false), 2500);
      }
      await refresh();
    } catch {
      /* best-effort */
    } finally {
      setClaiming(false);
    }
  };

  if (!data) return null;
  const { top3, is_sunday, my_placement, can_claim } = data;

  return (
    <div
      className="chunky-card p-4 mb-4"
      data-testid="weekly-reset-party"
      style={{ background: is_sunday ? "#FFE873" : "#EAF6FF" }}
    >
      {confetti && (
        <Confetti width={width} height={height} numberOfPieces={220} recycle={false} gravity={0.25} />
      )}
      <div className="flex items-center gap-2 mb-2">
        {is_sunday ? (
          <PartyPopper size={22} strokeWidth={3} color="#FF5A5F" />
        ) : (
          <Trophy size={22} strokeWidth={3} color="#FF5A5F" />
        )}
        <h2 className="font-display text-lg leading-none">
          {is_sunday ? "Weekly Reset Party — LIVE!" : "Weekly Reset Party"}
        </h2>
      </div>
      <p className="text-xs mb-3" style={{ color: "#484848" }}>
        {is_sunday
          ? "Top 3 kids of the week get 250 coins + a Weekly Champion sticker."
          : `Next reset in ${fmtCountdown(countdown)} (Sunday, UTC). Rack up playtime to make the podium!`}
      </p>
      {top3.length > 0 && (
        <ol className="flex gap-2 mb-3" data-testid="weekly-top3">
          {top3.map((row) => (
            <li
              key={row.visitor_id}
              className="chunky-card p-2 flex-1 text-center"
              style={{
                background: row.rank === 1 ? "#FFB400" : row.rank === 2 ? "#F1F4F7" : "#F5D1A8",
                minWidth: 0,
              }}
              data-testid={`weekly-top3-row-${row.rank}`}
            >
              <div className="text-xl leading-none">
                {row.rank === 1 ? "🥇" : row.rank === 2 ? "🥈" : "🥉"}
              </div>
              <div className="text-xl" aria-hidden="true">
                {row.avatar || "🐻"}
              </div>
              <div className="font-display text-sm truncate">{row.name}</div>
              <div className="text-xs" style={{ color: "#484848" }}>
                <span aria-hidden="true" style={{ marginRight: 2 }}>{flagOf(row.country_code)}</span>
                {Math.round(row.playtime_seconds / 60)}m
              </div>
            </li>
          ))}
        </ol>
      )}
      {is_sunday && my_placement && (
        <button
          onClick={claim}
          disabled={!can_claim || claiming}
          className="btn-3d w-full"
          data-testid="weekly-claim-btn"
          style={{
            background: can_claim ? "#54D62C" : "#7A8790",
            color: "#fff",
            // Override the global .btn-3d:disabled opacity so the claimed
            // state stays readable against the yellow Sunday card.
            opacity: 1,
          }}
        >
          <Sparkles size={18} strokeWidth={3} />
          {can_claim ? `Claim my #${my_placement} bonus (+250 coins)` : "Already claimed this week"}
        </button>
      )}
    </div>
  );
};

export default WeeklyResetParty;
